import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";

/**
 * Paths where firing a redirect-to-login would either loop or interrupt an
 * in-flight authentication. Anything that begins with one of these prefixes
 * is left alone.
 */
const LOGIN_PATH_PREFIXES = [
  "/login",
  "/enter",
  "/v2/login-with-email-password",
  "/v2/email-login-link",
  "/v2/reset-password",
  "/v2/sso-callback",
];

let isRedirecting = false;

/**
 * Send the browser to /login when a request to a private API fails because
 * the session is missing or rejected. Preserves the user's intended
 * destination in `?from=` so the post-login flow can land them back.
 *
 * Idempotent within a single page load: concurrent callers (e.g. multiple
 * in-flight requests all failing 401 at once) trigger exactly one
 * navigation. Skipped entirely while we are already on a login-related
 * route to avoid /login → /login churn.
 */
export function redirectToLogin(source: string): void {
  if (isRedirecting) {
    return;
  }

  const { pathname } = window.location;
  if (LOGIN_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return;
  }

  isRedirecting = true;
  const from = encodeURIComponent(pathname + window.location.search);

  logEvent(APP_V2_APP_EVENTS.NO_COGNITO_SESSION_REDIRECT_TO_LOGIN, {
    source,
    from: pathname,
  });

  window.location.href = `/login?from=${from}`;
}

/**
 * Test-only — resets the module-level idempotency flag so each test starts
 * from a clean state. Never call this from production code.
 */
export function resetRedirectToLoginStateForTesting(): void {
  isRedirecting = false;
}
